/** Route-specific bootstrap for prerendered public marketing pages. */

import { StrictMode, type ReactElement } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './ErrorBoundary';
import { marketingI18nReady } from '@/i18n/marketing';
import { TOOL_PAGE_LOADERS, loadFloorPlanLocales } from '@/tools/loaders';
import { initializeAnalyticsWhenIdle } from '@/landing/analytics-loader';
import '@/landing/landing.css';
import '@/tools/formula-scaler/formula-scaler.css';
import '@/tools/salon-price-list/salon-price-list.css';
import '@/tools/floor-plan/floor-plan-skeleton.css';
async function loadMarketingPage(pathname: string): Promise<ReactElement> {
  if (pathname === '/') {
    const { LandingPage } = await import('@/landing/LandingPage');
    return <LandingPage />;
  }

  if (pathname === '/tools') {
    // The hub grid renders every tool's card (title/description), including
    // floor-plan's, so its locale bundle must be ready before render too.
    const [{ ToolsHubPage }] = await Promise.all([
      import('@/tools/ToolsHubPage'),
      loadFloorPlanLocales(),
    ]);
    return <ToolsHubPage />;
  }

  if (pathname.startsWith('/tools/')) {
    const slug = pathname.slice('/tools/'.length).replace(/\/$/, '');
    const loader = TOOL_PAGE_LOADERS[slug];
    if (!loader) throw new Error(`Unknown tool page: ${slug}`);
    const [{ ToolPage }, { default: ToolComponent }] = await Promise.all([
      import('@/tools/ToolPage'),
      loader(),
    ]);
    return <ToolPage slug={slug} component={ToolComponent} />;
  }

  if (pathname.startsWith('/lp/')) {
    const slug = pathname.slice('/lp/'.length).replace(/\/$/, '');
    const { ExperimentLandingPage } = await import('@/landing/experiments/ExperimentLandingPage');
    return <ExperimentLandingPage slug={slug} />;
  }

  if (pathname === '/about') {
    const { AboutPage } = await import('@/pages/public/AboutPage');
    return <AboutPage />;
  }

  const slug = pathname.replace(/^\//, '').replace(/\/$/, '');
  const { SeoLandingPage } = await import('@/landing/seo-pages/SeoLandingPage');
  return <SeoLandingPage slug={slug} />;
}

async function bootstrap(): Promise<void> {
  await marketingI18nReady;

  const root = document.getElementById('root');
  if (!root) throw new Error('Root element not found');

  const page = await loadMarketingPage(window.location.pathname);
  const app = (
    <ErrorBoundary>
      <StrictMode>
        <HelmetProvider>{page}</HelmetProvider>
      </StrictMode>
    </ErrorBoundary>
  );

  if (root.hasChildNodes()) {
    hydrateRoot(root, app);
  } else {
    createRoot(root).render(app);
  }

  initializeAnalyticsWhenIdle();
}

void bootstrap();
